<script setup>
import { computed, ref } from 'vue'
import i18n from '../../plugins/i18n'
import { humanReadableTimestamp } from '@/composables/datetime'
import WYSIWYG from '@/components/WYSIWYG.vue'
import JsonEditorVue from 'json-editor-vue'
import 'vanilla-jsoneditor/themes/jse-theme-dark.css'
import { useSettingsStore } from '@/store/settings'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'

const { t } = i18n.global

const props = defineProps({ incident: { type: Object, default: () => ({}) } })

const settingsStore = useSettingsStore()
const openToolOutputIndex = ref(null)

const editorClass = computed(() => (settingsStore.theme === 'dark' ? 'jse-theme-dark' : ''))

const statusIcon = computed(() => {
  if (props.incident.state === 'closed') {
    return 'mdi-lock'
  }
  if (props.incident.state === 'open') {
    return 'mdi-lock-open-variant'
  }
  return 'mdi-information'
})

const humanReadableStatus = computed(() => {
  return t(`incidents.llmSummary.status.${props.incident.state}`)
})

const severityIcon = computed(() => {
  switch (props.incident.latestSeverity) {
    case 'critical':
      return 'mdi-alert-octagon'
    case 'warning':
      return 'mdi-alert'
    case 'informative':
      return 'mdi-information-outline'
    default:
      return 'mdi-help-circle-outline'
  }
})

const severityColor = computed(() => {
  switch (props.incident.latestSeverity) {
    case 'critical':
      return 'error'
    case 'warning':
      return 'warning'
    case 'informative':
      return 'info'
    default:
      return 'default'
  }
})

const humanReadableSeverity = computed(() => {
  return t(`incidents.llmSummary.severities.${props.incident.latestSeverity}`)
})

const stakeholders = computed(() => {
  return props.incident.stakeholder ?? []
})

const wasCreatedAutomatically = computed(() => {
  return props.incident.triggeredBy?.entityType !== 'account'
})

const toolOutputEntries = computed(() => {
  return Object.entries(props.incident.messagePayload?.toolOutputs ?? {})
})
</script>

<template>
  <v-layout-card>
    <div class="w-100 px-2 d-flex flex-row justify-space-between align-start flex-wrap">
      <div class="d-flex flex-column align-start">
        <v-card-title>
          <v-icon
            icon="mdi-creation"
            start
          />
          {{ $t('incidents.llmSummary.title') }}
        </v-card-title>
        <div class="mx-2 d-flex flex-row align-center ga-2 flex-wrap">
          <v-chip
            class="px-4"
            :prepend-icon="statusIcon"
            size="small"
            variant="flat"
            color="default"
          >
            {{ humanReadableStatus }}
          </v-chip>
          <v-chip
            v-if="incident.latestSeverity"
            class="px-4"
            :prepend-icon="severityIcon"
            size="small"
            variant="tonal"
            :color="severityColor"
          >
            {{ humanReadableSeverity }}
          </v-chip>
          <v-chip
            v-for="stakeholder in stakeholders"
            :key="stakeholder"
            class="px-4"
            prepend-icon="mdi-account-group"
            size="small"
            variant="tonal"
          >
            {{ $t(`incidents.genericProperties.stakeholder-elements.${stakeholder}`) }}
          </v-chip>
          <v-chip
            v-if="incident.autoClosed"
            class="px-4"
            prepend-icon="mdi-robot-outline"
            size="small"
            variant="tonal"
          >
            {{ $t('incidents.llmSummary.titles.auto_closed') }}
          </v-chip>
        </div>
      </div>

      <div class="align-self-start d-flex flex-row align-center ga-2">
        <slot name="jsonButton" />
        <slot name="commentButton" />
        <slot name="assignButton" />
      </div>
    </div>
    <v-card-text class="py-2">
      <v-list-item
        :title="$t('incidents.llmSummary.titles.timestamp')"
        :subtitle="humanReadableTimestamp(incident.firstEventTimestamp)"
        prepend-icon="mdi-clock"
      />
      <v-list-item
        :title="$t('incidents.llmSummary.titles.lastUpdatedTimestamp')"
        :subtitle="humanReadableTimestamp(incident.lastEventTimestamp)"
        prepend-icon="mdi-clock-end"
      />
      <v-list-item
        v-if="wasCreatedAutomatically"
        :title="$t('incidents.llmSummary.titles.created_automatically')"
        :subtitle="$t('incidents.llmSummary.properties.created_automatically')"
        prepend-icon="mdi-robot"
      />
      <v-list-item
        v-if="!wasCreatedAutomatically"
        :title="$t('incidents.llmSummary.titles.creator')"
        :subtitle="incident.triggeredBy.displayName"
        prepend-icon="mdi-account"
      />
      <v-list-item
        :title="$t('incidents.llmSummary.titles.summary')"
        prepend-icon="mdi-text-box"
      >
        <WYSIWYG
          :model-value="incident.messagePayload?.summary ?? ''"
          :read-only="true"
          hide-details
        />
      </v-list-item>
      <v-list-item
        v-if="toolOutputEntries.length > 0"
        :title="$t('incidents.llmSummary.titles.toolOutputs')"
        prepend-icon="mdi-tools"
      >
        <div class="d-flex flex-wrap ga-2 mt-2">
          <v-chip
            v-for="([name], index) in toolOutputEntries"
            :key="name"
            size="small"
            variant="tonal"
            color="primary"
            prepend-icon="mdi-function-variant"
            @click="openToolOutputIndex = index"
          >
            {{ name }}
            <GenericCustomDialog
              :model-value="openToolOutputIndex === index"
              max-width="700px"
              @update:model-value="value => (openToolOutputIndex = value ? index : null)"
            >
              <template #headerIcon><v-icon icon="mdi-function-variant" /></template>
              <template #title>{{ name }}</template>
              <template #body>
                <JsonEditorVue
                  :model-value="toolOutputEntries[index][1] ?? null"
                  mode="text"
                  :stringified="false"
                  :read-only="true"
                  :class="editorClass"
                />
              </template>
            </GenericCustomDialog>
          </v-chip>
        </div>
      </v-list-item>
      <v-list-item
        v-if="incident.state !== 'closed' && incident.isPublicOpen === false"
        prepend-icon="mdi-eye-off"
        :title="$t('incidents.titles.public_closed')"
        :subtitle="$t('incidents.properties.public_closed')"
      />
    </v-card-text>
    <v-card-actions class="justify-end">
      <slot
        name="genericActions"
        v-bind="{ enableNewEventButton: false }"
      />
    </v-card-actions>
  </v-layout-card>
</template>

<style scoped></style>
